import { useMemo, useState } from "react";
import {
  LayoutGrid,
  Circle,
  Tag,
  User,
  Calendar,
  ChevronDown,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  vendorChipClass,
  personChipClass,
  type Status as DirectoryStatus,
} from "./directory-data";
import { usePeople, useVendors } from "./use-directory";
import { useDeleteTask, type EditableTask } from "./use-tasks";
import { AddTaskDialog } from "./AddTaskDialog";
import { InlineAddTask } from "./InlineAddTask";
import { PoOrganizerPanel } from "./PoOrganizerPanel";

type Priority = "high" | "medium" | "low" | "urgent";
type Status = "todo" | "in_progress" | "waiting" | "done";

type TaggedRef = {
  id: number | string;
  name: string;
  slug: string;
  chip_color: string | null;
};

type RawOpsTask = {
  id: number | string;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  category: string | null;
  due_date: string | null;
  people: TaggedRef[];
  vendors: TaggedRef[];
};

type TaskCard = {
  id: number | string;
  title: string;
  description: string;
  vendors: TaggedRef[];
  people: TaggedRef[];
  priority: Priority;
  status: Status;
  category: string;
  dueDate?: string;
  raw: RawOpsTask;
};

const CATEGORIES = ["All", "Supplier", "Operations", "Product", "Compliance", "Finance"];
const PRIORITIES = ["All", "High", "Medium", "Low"];

const COLUMNS: { id: Status; label: string; color: string; dot: string }[] = [
  { id: "todo", label: "Todo", color: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
  { id: "in_progress", label: "In Progress", color: "bg-blue-50 text-blue-700", dot: "bg-blue-500" },
  { id: "waiting", label: "Waiting", color: "bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  { id: "done", label: "Done", color: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
];

const PRIORITY_COLORS: Record<Priority, string> = {
  high: "bg-red-500",
  medium: "bg-amber-400",
  low: "bg-emerald-400",
  urgent: "bg-red-600",
};

function FilterChips({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted/50 transition-colors"
      >
        <span className="text-muted-foreground">{label}:</span>
        <span className="text-foreground">{selected}</span>
        <ChevronDown className="w-3 h-3 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-background border border-border rounded-xl shadow-lg py-1 min-w-[150px]">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors ${
                selected === opt
                  ? "font-semibold text-foreground"
                  : "text-muted-foreground"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function normalizeStatus(s: string): Status {
  if (s === "review") return "in_progress";
  if (s === "todo" || s === "in_progress" || s === "waiting" || s === "done") return s;
  return "todo";
}

export function OperationsBoardPage() {
  const api = useApi();
  const peopleQ = usePeople();
  const vendorsQ = useVendors();
  const deleteTask = useDeleteTask();
  const [categoryFilter, setCategoryFilter] = useState("Operations");
  const [vendorFilter, setVendorFilter] = useState("All");
  const [personFilter, setPersonFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<EditableTask | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["operations_tasks"],
    queryFn: async (): Promise<RawOpsTask[]> => {
      const res = await api(`/api/operations_tasks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as RawOpsTask[];
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const tasks: TaskCard[] = useMemo(
    () =>
      (data ?? []).map((row) => ({
        id: row.id,
        title: row.title || "",
        description: row.description || "",
        vendors: row.vendors ?? [],
        people: row.people ?? [],
        priority: (row.priority as Priority) || "medium",
        status: normalizeStatus(row.status),
        category: row.category || "Operations",
        dueDate: row.due_date
          ? new Date(row.due_date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
          : undefined,
        raw: row,
      })),
    [data],
  );

  const peopleNames = ["All", ...(peopleQ.data ?? []).map((p) => p.name)];
  const vendorNames = ["All", ...(vendorsQ.data ?? []).map((v) => v.name)];

  const filtered = tasks.filter((task) => {
    if (categoryFilter !== "All" && task.category !== categoryFilter) return false;
    if (vendorFilter !== "All" && !task.vendors.some((v) => v.name === vendorFilter)) return false;
    if (personFilter !== "All" && !task.people.some((p) => p.name === personFilter)) return false;
    if (priorityFilter !== "All" && task.priority !== priorityFilter.toLowerCase()) return false;
    return true;
  });

  function openEditFor(task: TaskCard) {
    const editable: EditableTask = {
      id: task.id,
      title: task.title,
      description: task.description || null,
      status: task.status,
      priority: task.priority,
      category: task.category,
      due_date: task.raw.due_date,
      people: task.people.map((p) => ({ id: p.id, name: p.name })),
      vendors: task.vendors.map((v) => ({ id: v.id, name: v.name })),
    };
    setEditingTask(editable);
  }

  function confirmDelete(task: TaskCard) {
    const ok = window.confirm(
      `Delete \"${task.title || "untitled"}\"?\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    deleteTask.mutate(task.id);
  }

  return (
    <div
      className="flex flex-col h-full"
      style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}
    >
      <div className="px-8 py-5 border-b bg-background flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
            <LayoutGrid className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Operations Board</h1>
            <p className="text-xs text-muted-foreground">Flame King Asia — Yogev Hirsh</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Circle className="w-3.5 h-3.5 text-emerald-500 fill-emerald-500" />
            )}
            {isLoading ? "Loading..." : `${filtered.length} tasks`}
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1" />
            New task
          </Button>
        </div>
      </div>

      <div className="px-8 py-3 border-b bg-background flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Tag className="w-3.5 h-3.5" />
          <span>Filters</span>
        </div>
        <FilterChips
          label="Category"
          options={CATEGORIES}
          selected={categoryFilter}
          onChange={setCategoryFilter}
        />
        <FilterChips
          label="Vendor"
          options={vendorNames}
          selected={vendorFilter}
          onChange={setVendorFilter}
        />
        <FilterChips
          label="Person"
          options={peopleNames}
          selected={personFilter}
          onChange={setPersonFilter}
        />
        <FilterChips
          label="Priority"
          options={PRIORITIES}
          selected={priorityFilter}
          onChange={setPriorityFilter}
        />
        {(categoryFilter !== "Operations" ||
          vendorFilter !== "All" ||
          personFilter !== "All" ||
          priorityFilter !== "All") && (
          <button
            onClick={() => {
              setCategoryFilter("Operations");
              setVendorFilter("All");
              setPersonFilter("All");
              setPriorityFilter("All");
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline transition-colors"
          >
            Reset filters
          </button>
        )}
      </div>

      <PoOrganizerPanel />

      <div className="flex-1 overflow-auto bg-[#f8fafc] p-6">
        <div className="flex gap-4 h-full min-h-0" style={{ minWidth: "900px" }}>
          {COLUMNS.map((col) => {
            const colTasks = filtered.filter((t) => t.status === col.id);
            return (
              <div key={col.id} className="flex flex-col flex-1 min-w-[220px]">
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-3 ${col.color}`}
                >
                  <span className={`w-2 h-2 rounded-full ${col.dot}`} />
                  <span className="text-sm font-semibold">{col.label}</span>
                  <span className="ml-auto text-xs font-medium opacity-70">
                    {colTasks.length}
                  </span>
                </div>

                <div className="mb-2">
                  <InlineAddTask status={col.id as DirectoryStatus} />
                </div>

                <div className="flex flex-col gap-3 overflow-y-auto flex-1">
                  {colTasks.map((task) => (
                    <div
                      key={String(task.id)}
                      role="button"
                      tabIndex={0}
                      onClick={() => openEditFor(task)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openEditFor(task);
                        }
                      }}
                      className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-all cursor-pointer relative"
                    >
                      <button
                        type="button"
                        aria-label="Delete task"
                        title="Delete task"
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDelete(task);
                        }}
                        className="absolute top-2 right-2 p-1 rounded text-muted-foreground/60 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>

                      <div className="flex items-center gap-2 mb-2.5 pr-6">
                        <span
                          className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                            PRIORITY_COLORS[task.priority]
                          }`}
                          title={task.priority}
                        />
                        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          {task.category}
                        </span>
                      </div>

                      <p className="text-sm font-semibold text-foreground leading-snug mb-1.5">
                        {task.title}
                      </p>

                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3">
                        {task.description}
                      </p>

                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {task.vendors.map((v) => (
                          <span
                            key={String(v.id)}
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${vendorChipClass(
                              v.name,
                              v.chip_color,
                            )}`}
                          >
                            {v.name}
                          </span>
                        ))}
                        {task.people.map((p) => (
                          <span
                            key={String(p.id)}
                            className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${personChipClass(
                              p.name,
                              p.chip_color,
                            )}`}
                          >
                            <User className="w-2.5 h-2.5" />
                            {p.name}
                          </span>
                        ))}
                      </div>

                      {task.dueDate && (
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          {task.dueDate}
                        </div>
                      )}
                    </div>
                  ))}

                  {colTasks.length === 0 && (
                    <div className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border rounded-xl">
                      No tasks
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AddTaskDialog open={createOpen} onOpenChange={setCreateOpen} />

      <AddTaskDialog
        open={!!editingTask}
        onOpenChange={(open) => {
          if (!open) setEditingTask(null);
        }}
        task={editingTask ?? undefined}
      />
    </div>
  );
}

(OperationsBoardPage as unknown as { path: string }).path = "/operations";
